import type { DonationStatus } from './donation-status.js';

/**
 * The three sections of a campaign's operations page —
 * docs/decisions/0091. Each is a question the SQL answers, not a status of its
 * own: `waiting` is the waiting list (`pending` **and** ready for the queue,
 * `isOnWaitingList`), `queued` is the order and `fulfilled` is
 * the work carried out.
 */
export const OPERATION_STAGES = ['waiting', 'queued', 'fulfilled'] as const;
export type OperationStage = (typeof OPERATION_STAGES)[number];

/**
 * **Where a carried-out donation's evidence stands** — derived, never stored,
 * and never a donation status: the four statuses of docs/decisions/0077 stand
 * (docs/plans/12-operations-and-access.md, *Decisions taken*, 4).
 *
 * - `awaiting` — evidence awaited: nothing has been received for it, neither
 *   a file attached to the donation nor an inbound medium matched to it
 *   (the same test `donation.list`'s `hasEvidence` asks).
 * - `uploaded` — evidence received: evidence was received and no party has been
 *   sent it yet, while at least one party is still to be told.
 * - `completed` — complete: evidence was received and either a link to it
 *   has left for at least one party (an evidence link was issued, 0075), or the
 *   donation names nobody to tell — nothing is left to do.
 */
export const DONATION_EVIDENCE_STAGES = ['awaiting', 'uploaded', 'completed'] as const;
export type DonationEvidenceStage = (typeof DONATION_EVIDENCE_STAGES)[number];

export type EvidenceFacts = {
  readonly status: DonationStatus;
  /** A donation attachment, or a matched inbound medium. */
  readonly received: boolean;
  /** An evidence link of the donation was issued — its message left. */
  readonly sent: boolean;
  /** A `fulfilment` notice choice not opted out: somebody is to be told. */
  readonly awaitingParty: boolean;
};

/**
 * The evidence stage of one donation, or null for one not carried out. The SQL
 * filter over `donations` states the same rule; `operations.repository.test.ts`
 * holds the two to the same rows.
 */
export function evidenceStageOf(facts: EvidenceFacts): DonationEvidenceStage | null {
  if (facts.status !== 'fulfilled') return null;
  if (!facts.received) return 'awaiting';
  if (facts.sent || !facts.awaitingParty) return 'completed';
  return 'uploaded';
}

/**
 * An undo of a fulfilment says why, at least ten characters once trimmed —
 * the same bar a deletion's reason meets (docs/decisions/0085), because both
 * take back something the trail already records as done.
 */
export const DONATION_UNFULFILL_REASON_MIN_LENGTH = 10;
export const DONATION_UNFULFILL_REASON_MAX_LENGTH = 500;

export function acceptedUnfulfillReason(reason: string | null | undefined): string | null {
  const trimmed = reason?.trim() ?? '';
  if (trimmed.length < DONATION_UNFULFILL_REASON_MIN_LENGTH) return null;
  if (trimmed.length > DONATION_UNFULFILL_REASON_MAX_LENGTH) return null;
  return trimmed;
}

/**
 * Where an undone donation goes back to: the order, when it still holds the
 * place it was carried out from — fulfilment keeps `queue_position` — and
 * waiting otherwise, for work carried out without a queue.
 */
export function unfulfilledStatusFor(queuePosition: number | null): 'queued' | 'pending' {
  return queuePosition === null ? 'pending' : 'queued';
}

/**
 * Why a donation's fulfilment may not be taken back, asked under
 * its row lock — docs/decisions/0091:
 *
 * - `DONATION_UNFULFILL_REMAINDER_CANCELLED`: a cancelled remainder already cut
 *   the donation down to what its animals carried out (0074), so undoing the
 *   work would leave an amount priced for work that no longer stands;
 * - `DONATION_UNFULFILL_EVIDENCE_OFFERED`: its evidence was offered to a party
 *   — a link row exists, sent or on its way (0075) — so a person outside the
 *   organization may already have been shown the work as done, and a link row
 *   is never offered twice.
 */
export const DONATION_UNFULFILL_REFUSALS = [
  'DONATION_UNFULFILL_REMAINDER_CANCELLED',
  'DONATION_UNFULFILL_EVIDENCE_OFFERED',
] as const;
export type DonationUnfulfillRefusal = (typeof DONATION_UNFULFILL_REFUSALS)[number];

export function unfulfillRefusal(facts: {
  readonly remainderCancelled: boolean;
  readonly evidenceOffered: boolean;
}): DonationUnfulfillRefusal | null {
  if (facts.remainderCancelled) return 'DONATION_UNFULFILL_REMAINDER_CANCELLED';
  if (facts.evidenceOffered) return 'DONATION_UNFULFILL_EVIDENCE_OFFERED';
  return null;
}
